"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import heroShape from "@/assets/hero-shape.jpg";
import { CountUp } from "../CountUp";
import Image from "next/image";

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const statsContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.3 } },
};

export function Hero() {
  const shapeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!shapeRef.current) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 30;
      const y = (e.clientY / window.innerHeight - 0.5) * 30;
      shapeRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <section id="top" className="relative min-h-screen pt-32 pb-12 px-6 md:px-12 overflow-hidden flex flex-col">
      <div
        ref={shapeRef}
        className="absolute right-[-10%] top-[10%] w-[60vw] max-w-[900px] aspect-square pointer-events-none transition-transform duration-300 ease-out float"
      >
        {/* O Brilho (Glow) agora fica atrás da imagem e maior que ela para espalhar suavemente */}
        <div className="absolute inset-[0%] rounded-full acid-glow" />

        {/* A Imagem com mix-blend-mode ajustado */}
        <Image
          src={heroShape.src}
          alt="Abstract sculptural form"
          width={1280}
          height={1280}
          className="relative w-full h-full object-cover rounded-full opacity-95 mix-blend-lighten z-10"
        />
      </div>

      <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{ backgroundImage: "linear-gradient(to right, white 1px, transparent 1px)", backgroundSize: "8.333% 100%" }} />

      <div className="relative z-10 max-w-[1600px] mx-auto flex-1 flex flex-col justify-between w-full">
        <div>
          <motion.h1
            className="font-display uppercase leading-[0.82] tracking-[-0.03em] text-foreground"
            style={{ fontSize: "clamp(4rem, 14vw, 18rem)" }}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            Sound
            <br />
            That <span className="text-outline-acid italic font-serif normal-case tracking-tight">refuses</span>
            <br />
            To <span className="text-[oklch(0.94_0.27_128)]">quiet.</span>
          </motion.h1>

          <div className="mt-16 flex flex-col md:flex-row items-start md:items-end justify-between gap-10">
            <motion.p
              className="max-w-md font-serif text-2xl md:text-3xl leading-tight text-muted-foreground"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              A sonic studio for artists who'd rather shake the earth than blend in.
            </motion.p>

            <motion.div
              className="flex flex-col gap-4 items-start md:items-end"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <a href="#contact" className="btn-acid px-16 py-10 text-2xl md:text-3xl">
                <span>→ Start a Session</span>
              </a>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                2 slots remaining this quarter
              </span>
            </motion.div>
          </div>
        </div>

        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-x-20 gap-y-12 border-t border-border pt-12"
          variants={statsContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {[
            ["12+", "Years in the booth"],
            ["80M", "Streams reached"],
            ["3x", "Avg. Dynamic Range"],
            ["1", "Producer behind it all"],
          ].map(([k, v]) => (
            <motion.div key={k} variants={fadeUp}>
              <div className="font-display text-4xl md:text-6xl text-[oklch(0.94_0.27_128)]"><CountUp value={k} /></div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-2">{v}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
